import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Server, Cpu, HardDrive, Network, Clock, Activity, Database, Download, Loader2 } from 'lucide-react';
import SubdomainManagement from './SubdomainManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function ServerManagement() {
  const [serverInfo, setServerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);

  useEffect(() => {
    fetchServerInfo();
    fetchBackups();
    // Refresh every 5 seconds
    const interval = setInterval(fetchServerInfo, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchServerInfo = async () => {
    try {
      const response = await axios.get(`${API}/server-info`, getAuthHeaders());
      setServerInfo(response.data);
    } catch (error) {
      console.error('Error fetching server info:', error);
      if (loading) {
        toast.error('Ошибка загрузки информации о сервере');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await axios.get(`${API}/backup/list`, getAuthHeaders());
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  const createBackup = async () => {
    setBackingUp(true);
    try {
      const response = await axios.post(`${API}/backup/create`, {}, getAuthHeaders());
      toast.success(`Резервная копия создана: ${response.data.path}`);
      fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.detail || 'Ошибка создания резервной копии');
    } finally {
      setBackingUp(false);
    }
  };

  const getStatusColor = (percent) => {
    if (percent < 50) return 'text-green-600';
    if (percent < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (percent) => {
    if (percent < 50) return 'bg-green-500';
    if (percent < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-lg text-gray-600">Загрузка информации о сервере...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Server Info Card */}
      <Card className="glass-effect shadow-2xl border-2 border-purple-200 hover:border-purple-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-100">
          <CardTitle className="flex items-center text-2xl">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
              <Server className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            Информация о сервере
          </CardTitle>
          <CardDescription className="text-base font-medium text-gray-600">
            Мониторинг ресурсов и состояния сервера
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CPU */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Cpu className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 font-medium">CPU</div>
                    <div className="text-sm text-gray-500">{serverInfo?.cpu?.count || 0} ядер</div>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${getStatusColor(serverInfo?.cpu?.percent || 0)}`}>
                  {serverInfo?.cpu?.percent || 0}%
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${getProgressColor(serverInfo?.cpu?.percent || 0)}`}
                  style={{ width: `${serverInfo?.cpu?.percent || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Memory */}
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 font-medium">RAM</div>
                    <div className="text-sm text-gray-500">
                      {serverInfo?.memory?.used_gb || 0} / {serverInfo?.memory?.total_gb || 0} GB
                    </div>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${getStatusColor(serverInfo?.memory?.percent || 0)}`}>
                  {serverInfo?.memory?.percent || 0}%
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${getProgressColor(serverInfo?.memory?.percent || 0)}`}
                  style={{ width: `${serverInfo?.memory?.percent || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Disk */}
            <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <HardDrive className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 font-medium">Диск</div>
                    <div className="text-sm text-gray-500">
                      {serverInfo?.disk?.used_gb || 0} / {serverInfo?.disk?.total_gb || 0} GB
                    </div>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${getStatusColor(serverInfo?.disk?.percent || 0)}`}>
                  {serverInfo?.disk?.percent || 0}%
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${getProgressColor(serverInfo?.disk?.percent || 0)}`}
                  style={{ width: `${serverInfo?.disk?.percent || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Network */}
            <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Network className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 font-medium">Сеть</div>
                    <div className="text-sm text-gray-500">Трафик</div>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Отправлено:</span>
                  <span className="font-bold text-green-600">
                    {serverInfo?.network?.sent_mb || 0} MB
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Получено:</span>
                  <span className="font-bold text-blue-600">
                    {serverInfo?.network?.received_mb || 0} MB
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg">Информация о системе</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-600 font-medium">Платформа</div>
                <div className="text-gray-900 font-bold">
                  {serverInfo?.system?.platform || 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Версия</div>
                <div className="text-gray-900 font-bold">
                  {serverInfo?.system?.platform_version || 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Hostname</div>
                <div className="text-gray-900 font-bold truncate">
                  {serverInfo?.system?.hostname || 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 font-medium">Uptime</div>
                <div className="text-gray-900 font-bold">
                  {serverInfo?.system?.uptime_days || 0}д {serverInfo?.system?.uptime_hours || 0}ч
                </div>
              </div>
            </div>
          </div>

          {/* Auto-refresh indicator */}
          <div className="mt-4 text-center">
            <span className="text-xs text-gray-500 font-medium">
              🔄 Автоматическое обновление каждые 5 секунд
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Database Backup Card */}
      <Card className="glass-effect shadow-2xl border-2 border-green-200 hover:border-green-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-100">
          <CardTitle className="flex items-center text-2xl">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
              <Database className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            Резервное копирование БД
          </CardTitle>
          <CardDescription className="text-base font-medium text-gray-600">
            Сохранение базы данных в /var/www/bd
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button 
              onClick={createBackup} 
              disabled={backingUp}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold px-6 py-3"
            >
              {backingUp ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Создание копии...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Создать резервную копию
                </>
              )}
            </Button>
            <div className="text-sm text-gray-600">
              <p>Копия сохраняется в формате JSON</p>
              <p className="text-xs text-gray-500">Путь: /var/www/bd/busas_backup_[дата]</p>
            </div>
          </div>

          {/* Backup History */}
          {backups.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                История резервных копий
              </h3>
              <div className="space-y-2 max-h-[200px] overflow-auto">
                {backups.map((backup, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div>
                      <p className="font-medium text-gray-800">
                        {backup.timestamp?.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {backup.total_collections} коллекций
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {backup.folder_name}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const token = localStorage.getItem('token');
                          window.open(`${API}/backup/download/${backup.folder_name}?token=${token}`, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loadingBackups && (
            <div className="mt-4 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Загрузка истории...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subdomain Management */}
      <SubdomainManagement />
    </div>
  );
}
